import { Button, Divider, Modal, Radio, Segmented, Select, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faBrain,
  faCheck,
  faHeadSideBrain,
  faPaperclip,
  faTimes,
} from '@fortawesome/pro-light-svg-icons';
import { useCallback, useEffect, useState } from 'react';

enum Mode {
  ALL = 'ALL',
  NONE = 'NONE',
  SELECT = 'SELECT',
}

interface Props {
  options?: Option[];
  loading?: boolean;
  value?: string[] | null;
  onChange?: (value: string[] | null) => void;
  rounded?: boolean;
}

interface Option {
  label: string;
  value: string;
}

const controlOptions = [
  {
    label: (
      <div>
        <strong>Alle Wissensdatenbanken nutzen</strong>
        <div className="text-xs">
          Das gesamte Wissen aus allen verfügbaren Wissensdatenbanken des
          Unternehmens wird genutzt
        </div>
      </div>
    ),
    value: Mode.ALL,
  },
  {
    label: (
      <div>
        <strong>Wissensdatenbanken deaktivieren</strong>
        <div className="text-xs">
          Es wird kein Wissen des Unternehmens genutzt. Die KI benutzt nur
          eigenes Wissen oder Wissen, das durch Online-Recherche gewonnen wurde
        </div>
      </div>
    ),
    value: Mode.NONE,
  },
  {
    label: (
      <div>
        <strong>Wissensdatenbanken auswählen</strong>
        <div className="text-xs">
          Es wird nur das Wissen aus den ausgewählten Wissensdatenbanken genutzt
        </div>
      </div>
    ),
    value: Mode.SELECT,
  },
];

interface ModalProps {
  isOpen: boolean;
  value?: string[] | null;
  onChange?: (value: string[] | null) => void;
  onClose: () => void;
  options?: Option[];
  loading?: boolean;
}

const KnowledgeBasesSelectModal = ({
  isOpen,
  value,
  onChange,
  onClose,
  options,
  loading,
}: ModalProps) => {
  const [mode, setMode] = useState<Mode | undefined>();

  useEffect(() => {
    if (!value) {
      setMode(Mode.ALL);
    } else if (value?.length > 0) {
      setMode(Mode.SELECT);
    } else {
      setMode(Mode.NONE);
    }
  }, [value, isOpen]);

  const handleControlChange = useCallback(
    (e: any) => {
      if (!onChange) return;

      const newMode = e.target.value as Mode;

      if (newMode === Mode.ALL) onChange(null);
      if (newMode === Mode.NONE) onChange([]);

      setMode(newMode);
    },
    [onChange, value],
  );

  return (
    <Modal
      title="Firmenwissen"
      closable={{ 'aria-label': 'Schließen' }}
      open={isOpen}
      onCancel={onClose}
      className="rounded-2xl"
      classNames={{
        content: '!rounded-2xl',
      }}
      footer={null}
    >
      <Radio.Group
        value={mode}
        options={controlOptions}
        className="space-y-5"
        onChange={handleControlChange}
      />

      {mode === Mode.SELECT && (
        <Select
          options={options}
          loading={loading}
          value={value}
          onChange={onChange}
          mode="tags"
          className="mt-2 w-full"
        />
      )}
    </Modal>
  );
};

const KnowledgeBasesSelect = ({
  options,
  loading,
  rounded,
  value,
  onChange,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Button
        icon={<FontAwesomeIcon icon={faHeadSideBrain} />}
        className={
          rounded
            ? 'rounded-full h-[35px] border-gray-300 text-gray-600 border'
            : ''
        }
        onClick={() => setIsModalOpen(true)}
      >
        <span className="max-w-[200px] overflow-ellipsis overflow-hidden text-nowrap">
          {value
            ? value.length > 0
              ? `${value.length} ausgewählt`
              : 'Kein Firmenwissen'
            : 'Firmenwissen'}
        </span>
      </Button>

      <KnowledgeBasesSelectModal
        isOpen={isModalOpen}
        value={value}
        onChange={onChange}
        onClose={() => setIsModalOpen(false)}
        options={options}
        loading={loading}
      />
    </>
  );
};

export default KnowledgeBasesSelect;
